<template>
    <v-dialog :value="value" max-width="1100" scrollable @input="$emit('input', $event)">
        <v-card v-if="request">
            <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px;">
                <span>{{ request.user && request.user.name }} — {{ (request.leave_type && request.leave_type.name) || (isWfh ? 'Work From Home' : 'Leave') }}</span>
                <v-spacer />
                <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <div class="text-body-2 mb-3">
                    {{ rangeLabel }}
                    <span v-if="request.note" class="grey--text font-italic"> · “{{ request.note }}”</span>
                </div>

                <v-select
                    v-if="companyOptions.length > 1"
                    v-model="companyId"
                    :items="companyOptions"
                    item-text="name"
                    item-value="id"
                    label="Company"
                    outlined
                    dense
                    hide-details
                    class="mb-3"
                    style="max-width: 320px;"
                />

                <v-alert v-if="noCompany" type="info" dense text>
                    This person isn't a member of any company you can check, so there is no business unit calendar to show.
                </v-alert>
                <v-alert v-else-if="forbidden" type="warning" dense text>
                    You don't have access to this company's calendar. You can still approve or decline below.
                </v-alert>
                <v-alert v-else-if="error" type="error" dense text>
                    Couldn't load the business unit calendar.
                    <v-btn small text class="text-none" @click="load">Retry</v-btn>
                </v-alert>
                <v-skeleton-loader v-else-if="loading" type="card" />

                <template v-else-if="loaded">
                    <v-alert :type="summary.offCount > 0 ? 'warning' : 'success'" dense text class="mb-3">
                        <strong>{{ summary.offCount }} of {{ summary.totalMembers }}</strong>
                        {{ companyName }} members already on leave during these dates
                        <span v-if="summary.pendingOthers"> · {{ summary.pendingOthers }} more pending</span>
                        <div v-if="peakDay" class="text-caption">
                            Busiest day: {{ peakDay.label }} ({{ peakDay.count }} off)
                        </div>
                    </v-alert>
                    <LeaveWallchartGrid
                        :days="days"
                        :rows="rows"
                        :legend-types="legendTypes"
                        :color-for-type="colorForType"
                        :highlight-user-id="requesterId"
                        :highlight-from="fromKey"
                        :highlight-to="toKey"
                        empty-text="No members to show"
                        legend-empty-text="No leave in this period"
                    />
                </template>

                <v-textarea v-model="note" outlined dense rows="2" hide-details class="mt-4"
                    placeholder="Add a note for the employee (optional)" />
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-spacer />
                <v-btn outlined color="error" class="text-none" :loading="updating" @click="$emit('decline', note)">Decline</v-btn>
                <v-btn color="success" dark class="text-none" :loading="updating" @click="$emit('approve', note)">Approve</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import LeaveWallchartGrid from './LeaveWallchartGrid.vue';
import {
    buildDays,
    buildWallchartRows,
    summarizeConflicts,
    colorForType,
    requestUserId,
    isLeaveSuperAdmin,
    managedCompanyIds,
} from './leaveUtils';

const PAD_DAYS = 3;
const MAX_RANGE_DAYS = 62;

export default {
    name: 'LeaveConflictDialog',
    components: { LeaveWallchartGrid },
    inject: ['users'],
    props: {
        value: { type: Boolean, default: false },
        request: { type: Object, default: null },
        isWfh: { type: Boolean, default: false },
        updating: { type: Boolean, default: false },
        initialNote: { type: String, default: '' },
    },
    data() {
        return {
            companyId: null,
            loading: false,
            loaded: false,
            error: false,
            forbidden: false,
            memberIds: [],
            requests: [],
            leaveTypes: [],
            note: '',
            loadSeq: 0,
        };
    },
    computed: {
        ...mapGetters({ me: 'auth/get_user' }),
        requesterId() {
            if (!this.request) return null;
            const id = Number(requestUserId(this.request));
            return Number.isFinite(id) ? id : null;
        },
        fromKey() {
            return this.request ? (this.request.from || '').substring(0, 10) : null;
        },
        toKey() {
            return this.request ? (this.request.to || '').substring(0, 10) : null;
        },
        rangeLabel() {
            if (!this.request) return '';
            const f = this.$date(this.fromKey).format('MMM D, YYYY');
            const t = this.$date(this.toKey).format('MMM D, YYYY');
            return this.fromKey === this.toKey ? f : `${f} – ${t}`;
        },
        // Companies the requester belongs to AND the viewer may manage (Super
        // Admin / manager roles see all of the requester's companies).
        companyOptions() {
            const list = this.users() || [];
            const requester = list.find((u) => Number(u.id) === this.requesterId);
            const viewer = list.find((u) => Number(u.id) === Number(this.me && this.me.id));
            const privileged = isLeaveSuperAdmin(this.me);
            const managed = managedCompanyIds(viewer);
            return ((requester && requester.access) || [])
                .filter((a) => privileged || managed.has(Number(a.company_id)))
                .map((a) => ({ id: Number(a.company_id), name: (a.company && a.company.name) || `Company #${a.company_id}` }));
        },
        noCompany() {
            return this.companyOptions.length === 0;
        },
        companyName() {
            const c = this.companyOptions.find((o) => o.id === this.companyId);
            return c ? c.name : 'company';
        },
        windowStart() {
            return this.$date(this.fromKey).subtract(PAD_DAYS, 'day').format('YYYY-MM-DD');
        },
        windowEnd() {
            return this.$date(this.toKey).add(PAD_DAYS, 'day').format('YYYY-MM-DD');
        },
        // API rejects ranges over 62 days; clamp what we fetch.
        fetchEnd() {
            const cap = this.$date(this.windowStart).add(MAX_RANGE_DAYS, 'day').format('YYYY-MM-DD');
            return this.windowEnd < cap ? this.windowEnd : cap;
        },
        days() {
            const count = Math.min(this.$date(this.windowEnd).diff(this.$date(this.windowStart), 'day') + 1, 31);
            return buildDays(this.windowStart, count, this.$date().format('YYYY-MM-DD'));
        },
        members() {
            const ids = new Set(this.memberIds);
            return (this.users() || [])
                .filter((u) => ids.has(Number(u.id)) && !u.deleted_at)
                .map((u) => ({ ...u, role_name: (u.role && u.role.name) || u.role_name }))
                .sort((a, b) => {
                    // requester first, then alphabetical
                    if (Number(a.id) === this.requesterId) return -1;
                    if (Number(b.id) === this.requesterId) return 1;
                    return (a.name || '').localeCompare(b.name || '');
                });
        },
        rows() {
            return buildWallchartRows({
                users: this.members,
                requests: this.requests,
                days: this.days,
                leaveTypes: this.leaveTypes,
                includePending: true,
            });
        },
        summary() {
            return summarizeConflicts({
                memberIds: this.memberIds,
                requests: this.requests,
                from: this.fromKey,
                to: this.toKey < this.fetchEnd ? this.toKey : this.fetchEnd,
                leaveTypes: this.leaveTypes,
                requesterId: this.requesterId,
                excludeRequestId: this.request ? this.request.id : null,
            });
        },
        peakDay() {
            // perDay counts people off that day; a half-day counts as a full person-day.
            const entries = Object.entries(this.summary.perDay);
            if (!entries.length) return null;
            const [key, count] = entries.sort((a, b) => b[1] - a[1])[0];
            return { count, label: this.$date(key).format('ddd, MMM D') };
        },
        legendTypes() {
            const ids = new Set(this.requests.map((r) => r.leave_type_id));
            return this.leaveTypes.filter((lt) => ids.has(lt.id));
        },
    },
    watch: {
        value(open) {
            if (!open) return;
            this.note = this.initialNote || '';
            this.loaded = false;
            this.error = false;
            this.forbidden = false;
            const first = this.companyOptions.length ? this.companyOptions[0].id : null;
            const same = first === this.companyId;
            this.companyId = first;
            // the companyId watcher only fires on change; load explicitly otherwise
            if (first && same) this.load();
        },
        companyId(id, old) {
            if (this.value && id && id !== old) this.load();
        },
    },
    methods: {
        colorForType(id) {
            return colorForType(id, this.leaveTypes);
        },
        async load() {
            if (!this.companyId || !this.request) return;
            const seq = ++this.loadSeq;
            this.loading = true;
            this.loaded = false;
            this.error = false;
            this.forbidden = false;
            try {
                const [cal, types] = await Promise.all([
                    this.$axios.get(`/api/leave/company_calendar/${this.companyId}`, {
                        from: this.windowStart,
                        to: this.fetchEnd,
                    }),
                    this.leaveTypes.length ? Promise.resolve(null) : this.$axios.get('/api/leave/type'),
                ]);
                if (seq !== this.loadSeq) return; // a newer load superseded this one
                if (cal && cal.status === 403) {
                    this.forbidden = true;
                    return;
                }
                if (!cal || !Array.isArray(cal.data && cal.data.member_ids)) {
                    this.error = true;
                    return;
                }
                this.memberIds = cal.data.member_ids.map(Number);
                this.requests = (cal.data.requests || []).map((r) => ({ ...r, user_id: Number(requestUserId(r)) }));
                // /api/leave/type returns a plain array (see LeaveManagementComponent.getLeaveTypes)
                if (types && types.data) this.leaveTypes = Array.isArray(types.data) ? types.data : (types.data.data || []);
                this.loaded = true;
            } catch (e) {
                if (seq === this.loadSeq) this.error = true;
            } finally {
                if (seq === this.loadSeq) this.loading = false;
            }
        },
    },
};
</script>
